<div class="chat-msg"
     [class.user]="isUser"
     [class.assistant]="isAssistant"
     [class.system]="isSystem"
     [attr.aria-label]="isUser ? ('chatAi.message.you' | translate) : ('chatAi.message.assistant' | translate)">

  <!-- V2: Avatar with thinking animation -->
  <img *ngIf="isAssistant"
       class="chat-msg__avatar"
       [class.chat-msg__avatar--thinking]="message.isStreaming"
       src="assets/cafler-avatar.svg" alt="" aria-hidden="true" />

  <!-- ── User: column with attachment chips + body ── -->
  <div *ngIf="isUser" class="chat-msg__user-col">
    <!-- Attachment chips above the user bubble -->
    <div class="chat-msg__attachments" *ngIf="message.attachments?.length">
      <button *ngFor="let att of message.attachments"
              class="chat-msg__att-chip"
              type="button"
              [disabled]="att.mediaType === 'error'"
              (click)="openPreview(att)">
        {{ attachmentChipLabel(att) }}
      </button>
    </div>
    <div class="chat-msg__body">
      <ng-container *ngTemplateOutlet="segmentsContent"></ng-container>
    </div>
  </div>

  <!-- ── Assistant / system body ── -->
  <div class="chat-msg__body" *ngIf="!isUser" #msgBody>
    <ng-container *ngTemplateOutlet="segmentsContent"></ng-container>
    <!-- Agent attribution badge — shown after streaming completes -->
    <span *ngIf="isAssistant && !message.isStreaming && message.agentCode"
          class="chat-msg__agent-badge">
      {{ message.agentCode }}
    </span>
  </div>

</div>

<!-- ── Attachment preview modal ── -->
<div class="chat-att-modal" *ngIf="previewAttachment" (click)="closePreview()">
  <div class="chat-att-modal__box" (click)="$event.stopPropagation()">
    <div class="chat-att-modal__header">
      <span class="chat-att-modal__title">{{ previewAttachment.fileName }}</span>
      <button class="chat-att-modal__close" type="button" (click)="closePreview()" aria-label="Cerrar">✕</button>
    </div>
    <div class="chat-att-modal__content">
      <!-- Image -->
      <img *ngIf="isImageAtt(previewAttachment)"
           class="chat-att-modal__img"
           [src]="previewImgUrl(previewAttachment)"
           [alt]="previewAttachment.fileName" />
      <!-- PDF -->
      <iframe *ngIf="isPdfAtt(previewAttachment)"
              class="chat-att-modal__iframe"
              [src]="previewPdfUrl(previewAttachment)"
              frameborder="0">
      </iframe>
      <!-- Text / CSV / Excel -->
      <pre *ngIf="isTextAtt(previewAttachment)" class="chat-att-modal__text">{{ previewAttachment.textContent }}</pre>
    </div>
  </div>
</div>

<!-- ── Shared segments template ── -->
<ng-template #segmentsContent>
  <ng-container *ngFor="let seg of segments; trackBy: trackByIndex">

    <!-- Tool activity chip -->
    <div class="chat-msg__tool"
         *ngIf="seg.kind === 'tool_activity'"
         [class.running]="seg.toolStatus === 'running'"
         [class.done]="seg.toolStatus === 'done'"
         [class.error]="seg.toolStatus === 'error'">
      <span class="chat-msg__tool-icon" *ngIf="seg.toolStatus === 'running'">
        <span class="chat-msg__tool-spinner"></span>
      </span>
      <span class="chat-msg__tool-icon done" *ngIf="seg.toolStatus === 'done'">&#10003;</span>
      <span class="chat-msg__tool-icon error" *ngIf="seg.toolStatus === 'error'">&#10007;</span>
      <span class="chat-msg__tool-label">{{ seg.content }}</span>
    </div>

    <!-- Text segment: parse into HTML / chart / actions blocks -->
    <ng-container *ngIf="seg.kind === 'text' && seg.content">
      <ng-container *ngFor="let block of parseTextBlocks(seg.content, message.isStreaming); trackBy: trackByIndex">

        <!-- HTML block -->
        <div class="chat-msg__html" *ngIf="block.kind === 'html'" [innerHTML]="block.safeHtml"></div>

        <!-- Chart block -->
        <app-chat-mini-chart *ngIf="block.kind === 'chart'" [spec]="block.value"></app-chat-mini-chart>

        <!-- Action buttons -->
        <div class="chat-msg__actions" *ngIf="block.kind === 'actions'">
          <button *ngFor="let action of parseActions(block.value)"
                  class="chat-msg__cta"
                  type="button"
                  (click)="navigateTo(action.route)">
            {{ action.label }}
          </button>
        </div>

        <!-- Financial report (CASH) — downloadable table -->
        <app-chat-financial-report *ngIf="block.kind === 'report' && !message.isStreaming"
                                   [spec]="block.value">
        </app-chat-financial-report>

        <!-- KPI tiles grid (CASH) — replaces inline HTML grid -->
        <app-chat-kpi-tiles *ngIf="block.kind === 'kpi_tiles' && !message.isStreaming"
                            [spec]="block.value">
        </app-chat-kpi-tiles>

        <!-- Product card widget -->
        <app-chat-product-card *ngIf="block.kind === 'product_card' && !message.isStreaming"
                               [spec]="block.value"
                               (purchase)="suggestionPick.emit($event)">
        </app-chat-product-card>

        <!-- Onboarding guide (stepper with media) -->
        <app-chat-onboarding-guide *ngIf="block.kind === 'onboarding_guide' && !message.isStreaming"
                                   [spec]="block.value">
        </app-chat-onboarding-guide>

        <!-- Loading placeholder — shown while a rich block (onboarding_guide, report, etc.) is still streaming -->
        <div class="chat-msg__block-loading" *ngIf="block.kind === 'loading'">
          <span></span><span></span><span></span>
        </div>

        <!-- Suggestion chips -->
        <div class="chat-msg__suggestions" *ngIf="block.kind === 'suggestions' && !message.isStreaming">
          <button *ngFor="let sug of parseSuggestions(block.value)"
                  class="chat-msg__suggestion-chip"
                  type="button"
                  (click)="suggestionPick.emit(sug)">
            {{ sug }}
          </button>
        </div>

        <!-- CSV download card — shown instead of dumping CSV inline -->
        <div class="chat-msg__csv-card" *ngIf="block.kind === 'csv' && !message.isStreaming">
          <div class="chat-msg__csv-info">
            <svg class="chat-msg__csv-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            <div class="chat-msg__csv-meta">
              <strong>plantilla.csv</strong>
              <span class="chat-msg__csv-cols">{{ csvHeaderPreview(block.value) }}</span>
              <span class="chat-msg__csv-rows">{{ csvRowCount(block.value) }} filas</span>
            </div>
          </div>
          <button type="button"
                  class="chat-msg__csv-btn"
                  (click)="downloadCsv(block.value)">
            Descargar CSV
          </button>
        </div>

      </ng-container>
    </ng-container>

  </ng-container>

  <!-- V1: Blinking cursor while streaming text -->
  <span class="chat-msg__cursor" *ngIf="message.isStreaming && message.content"></span>

  <!-- Streaming dots — only when no content yet -->
  <span class="chat-msg__dots" *ngIf="message.isStreaming && !message.content" aria-live="polite">
    <span></span><span></span><span></span>
  </span>

  <!-- Retry button — shown on retryable errors (e.g. rate limit) -->
  <button *ngIf="message.retryPayload"
          type="button"
          class="chat-msg__retry-btn"
          (click)="retry.emit(message.retryPayload!)">
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
      <path d="M3 3v5h5"/>
    </svg>
    {{ 'chatAi.errors.retry' | translate }}
  </button>

  <span class="chat-msg__time" *ngIf="timestamp">{{ timestamp }}</span>
</ng-template>
